import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  Card,
  CardContent,
  Typography,
  Collapse,
  Alert,
  CircularProgress,
  Snackbar,
  Tooltip,
  TablePagination,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ExpandMore,
  ExpandLess,
  Save,
  Cancel,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../api/admin';
import { TrainingSet } from '../state/atoms';
import ConfirmDialog from './ConfirmDialog';

interface TrainingManagerProps {
  onUpdate?: () => void;
}

const TrainingManager: React.FC<TrainingManagerProps> = ({ onUpdate }) => {
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSet, setEditingSet] = useState<TrainingSet | null>(null);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({ open: false, message: '', severity: 'success' });

  const { data, isLoading, error } = useQuery({
    queryKey: ['trainingSets'],
    queryFn: () => adminAPI.getTrainingSets(),
  });

  const createMutation = useMutation({
    mutationFn: (data: { question: string; answer: string }) =>
      adminAPI.createTrainingSet(data.question, data.answer),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingSets'] });
      onUpdate?.();
      handleCloseDialog();
      setSnackbar({ open: true, message: 'Training set created', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to create training set', severity: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; question: string; answer: string }) =>
      adminAPI.updateTrainingSet(data.id, data.question, data.answer),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingSets'] });
      onUpdate?.();
      handleCloseDialog();
      setSnackbar({ open: true, message: 'Training set updated', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to update training set', severity: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminAPI.deleteTrainingSet(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingSets'] });
      onUpdate?.();
      setSnackbar({ open: true, message: 'Training set deleted', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to delete training set', severity: 'error' });
    },
  });

  const handleOpenDialog = (set?: TrainingSet) => {
    if (set) {
      setEditingSet(set);
      setQuestion(set.question);
      setAnswer(set.answer);
    } else {
      setEditingSet(null);
      setQuestion('');
      setAnswer('');
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSet(null);
    setQuestion('');
    setAnswer('');
  };

  const handleSave = () => {
    if (!question.trim() || !answer.trim()) {
      return;
    }

    if (editingSet) {
      updateMutation.mutate({
        id: editingSet.id,
        question: question.trim(),
        answer: answer.trim(),
      });
    } else {
      createMutation.mutate({
        question: question.trim(),
        answer: answer.trim(),
      });
    }
  };

  const openDeleteConfirm = (id: string) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };

  const confirmDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
    }
    closeDeleteConfirm();
  };

  const trainingSets = data?.trainingSets || [];
  // Adjust page if data size changes to avoid empty pages
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(trainingSets.length / rowsPerPage));
    if (page > totalPages - 1) {
      setPage(totalPages - 1);
    }
  }, [trainingSets.length, rowsPerPage, page]);

  const pagedTrainingSets = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return trainingSets.slice(start, end);
  }, [trainingSets, page, rowsPerPage]);
  const canAddMore = trainingSets.length < 100;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load training sets. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ 
        mb: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 0 }
      }}>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          {trainingSets.length} of 100 training sets used
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          disabled={!canAddMore}
          sx={{
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            px: { xs: 2, sm: 3 },
            py: { xs: 1, sm: 1.5 },
            borderColor: "#FFED00",
            color: "#FFED00",
            "&:hover": {
              borderColor: "#FFF27A",
              background: "rgba(255, 237, 0, 0.1)"
            },
            "&:disabled": {
              borderColor: "#3D474E",
              color: "#3D474E"
            }
          }}
        >
          Add Training Set
        </Button>
      </Box>

      {!canAddMore && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You've reached the maximum of 100 training sets. Delete some to add more.
        </Alert>
      )}

      <List sx={{ maxHeight: { xs: '400px', sm: '600px' }, overflow: 'auto' }}>
        {pagedTrainingSets.map((set, index) => (
          <Card
            key={set.id}
            sx={{
              mb: 2,
              '&:hover': {
                boxShadow: 4,
              },
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: { xs: 1, sm: 2 },
                flexDirection: { xs: 'column', sm: 'row' }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  <Typography
                    variant="h6"
                    sx={{
                      minWidth: '30px',
                      color: 'primary.main',
                      fontWeight: 'bold',
                      fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}
                  >
                    #{page * rowsPerPage + index + 1}
                  </Typography>

                  {/* Mobile Action Buttons */}
                  <Box sx={{ 
                    display: { xs: 'flex', sm: 'none' }, 
                    alignItems: 'center', 
                    gap: 0.5 
                  }}>
                    <Tooltip title={expandedId === set.id ? 'Hide answer' : 'Show answer'}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setExpandedId(expandedId === set.id ? null : set.id)
                        }
                      >
                        {expandedId === set.id ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDialog(set)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openDeleteConfirm(set.id)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box sx={{ 
                  flexGrow: 1,
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  <Typography 
                    variant="subtitle1" 
                    fontWeight="bold" 
                    gutterBottom
                    sx={{ 
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      wordBreak: 'break-word'
                    }}
                  >
                    Q: {set.question}
                  </Typography>

                  <Collapse in={expandedId === set.id}>
                    <Box
                      sx={{
                        mt: 2,
                        p: { xs: 1.5, sm: 2 },
                        bgcolor: 'rgba(61, 71, 78, 0.2)',
                        border: '1px solid rgba(61, 71, 78, 0.3)',
                        borderRadius: 2,
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        gutterBottom
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        Answer:
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          wordBreak: 'break-word'
                        }}
                      >
                        {set.answer}
                      </Typography>
                    </Box>
                  </Collapse>
                </Box>

                {/* Desktop Action Buttons */}
                <Box sx={{ 
                  display: { xs: 'none', sm: 'flex' }, 
                  alignItems: 'center', 
                  gap: 0.5 
                }}>
                  <Tooltip title={expandedId === set.id ? 'Hide answer' : 'Show answer'}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setExpandedId(expandedId === set.id ? null : set.id)
                      }
                    >
                      {expandedId === set.id ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(set)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => openDeleteConfirm(set.id)}
                      sx={{ color: '#3D474E', '&:hover': { color: '#FFED00', bgcolor: 'rgba(255, 237, 0, 0.1)' } }}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </List>

      <TablePagination
        component="div"
        count={trainingSets.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
        labelRowsPerPage="Per page"
      />

      {trainingSets.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: 'text.secondary',
          }}
        >
          <Typography variant="h6" gutterBottom>
            No Training Sets Yet
          </Typography>
          <Typography variant="body2">
            Click "Add Training Set" to create your first Q&A pair
          </Typography>
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingSet ? 'Edit Training Set' : 'Add New Training Set'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            multiline
            rows={2}
            sx={{ mt: 2, mb: 2 }}
            placeholder="What question should the AI be able to answer?"
          />
          <TextField
            fullWidth
            label="Answer"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            multiline
            rows={4}
            placeholder="Provide a clear and complete answer..."
          />
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<Cancel />}
            onClick={handleCloseDialog}
          >
            Cancel
          </Button>
          <Button
            startIcon={<Save />}
            variant="outlined"
            onClick={handleSave}
            disabled={!question.trim() || !answer.trim() || createMutation.isPending || updateMutation.isPending}
            sx={{
              borderColor: "#FFED00",
              color: "#FFED00",
              "&:hover": {
                borderColor: "#FFF27A",
                background: "rgba(255, 237, 0, 0.1)"
              },
              "&:disabled": {
                borderColor: "#3D474E",
                color: "#3D474E"
              }
            }}
          >
            {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={confirmOpen}
        onClose={closeDeleteConfirm}
        onConfirm={confirmDelete}
        title="Delete training set?"
        description="This action cannot be undone. The Q&A pair will be permanently removed."
        confirmText="Delete"
        confirmColor="secondary"
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TrainingManager;

